/**
 * "See my data" on `/settings/data` (#585, founder decision 2026-11-01).
 *
 * Israeli PPL section 13 inspection is met by showing a learner what is kept about them, on screen. #585 replaced the
 * downloadable archive for learners with this; #643 restored a download beside it: a ZIP of exactly these records, in full, with
 * the learner's pages (the same server read model, so it withholds exactly what this view withholds). Four tabs, and the split between the first two is invariant 2 made visible:
 *   - What you did: observations, in a plain solid frame.
 *   - What DaVinci concluded: inferences, in a dashed frame and each row introduced as DaVinci's conclusion, so the two are
 *     told apart by shape and by words, never by colour alone. Every conclusion shows the policy version it was derived under.
 *   - Your pages: the learner's own uploaded pages, as the existing page-image endpoint serves them.
 *   - Identity and consents.
 *
 * It fetches nothing on load: the section asks for the data only when the learner presses the button, like the deletion
 * preview, so opening Settings reads nothing. A row the server withheld (a reason, a check's certificate, a practice answer, a
 * delivered hint, a flagged line the learner has not opened) is shown as existing, with where its content is seen: on its own page,
 * where seeing it counts as help. The "What DaVinci concluded" tab also states what a conclusion is scoped to (SEC-027). Nothing
 * here is a percentage or a score; no count is shown.
 */

import { useId, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { MyDataResponse, MyDataTable } from '@davinci/contracts';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { saveFile } from './save-file.js';
import { friendlyTableLabel } from './table-labels.js';

type TabId = 'observations' | 'inferences' | 'pages' | 'identity';
const TABS: readonly TabId[] = ['observations', 'inferences', 'pages', 'identity'];

const messages = defineMessages({
  en: {
    heading: 'See my data',
    intro:
      'Everything DaVinci keeps about you, shown here on the screen. What you did and what DaVinci concluded from it are kept apart. Nothing here changes when you look.',
    show: 'See my data',
    download: 'Download my data',
    downloading: 'Preparing your download…',
    downloadIntro:
      'The download is a ZIP file of the same records, in full, with the homework pages you uploaded. Like this view, it leaves out DaVinci’s answers, hints and feedback reasons, which you see on their own pages, and DaVinci’s own configuration. It is built when you ask and is not kept afterwards.',
    hide: 'Hide my data',
    loading: 'Loading your data…',
    tabsLabel: 'My data',
    observations: 'What you did',
    inferences: 'What DaVinci concluded',
    pages: 'Your pages',
    identity: 'Identity and consents',
    observationsNote:
      'Records of what you actually did: uploads, confirmations and corrections, help you opened, what you said about your own confidence, disputes and practice attempts.',
    inferencesNote:
      'Conclusions DaVinci drew from what you did. Each one names the policy version it was derived under. These are DaVinci’s conclusions, not things you did.',
    conclusion: 'DaVinci’s conclusion',
    policyVersion: 'Policy version',
    withheldFeedback: 'Not opened yet. Open it on its feedback page; seeing it there counts as help.',
    withheldCheck: 'Not opened yet. Open it on its check page; seeing it there counts as help.',
    withheldContent:
      'DaVinci keeps this, but it is not shown here, because showing it would be help. Open it where the product offers it (the feedback page, the check, or the practice item); seeing it there counts as help.',
    withheldSource: 'Text taken from a textbook or other source is kept but not shown here.',
    truncated: 'Only the first rows of this table are shown.',
    empty: 'Nothing is kept here.',
    pagesNote: 'The pages you uploaded, as DaVinci holds them. This is your own handwriting.',
    openImage: 'Open the page image',
    pageAlt: (n: string) => `Uploaded page ${n}`,
    account: 'Your account',
    username: 'Username',
    displayName: 'Display name',
    language: 'Language',
    otherRecords: 'Other records of yours',
    consents: 'Your consent decisions',
    purpose: 'Purpose',
    decision: 'Decision',
    statement: 'Statement version',
  },
  he: {
    heading: 'הצגת הנתונים שלי',
    intro:
      'כאן מוצג על המסך כל מה ש-DaVinci שומר עליך. מה שנעשה ומה ש-DaVinci הסיק ממנו מופרדים זה מזה. שום דבר לא משתנה כשמציגים אותו.',
    show: 'הצגת הנתונים שלי',
    download: 'הורדת הנתונים שלי',
    downloading: 'ההורדה בהכנה…',
    downloadIntro:
      'ההורדה היא קובץ ZIP עם אותן רשומות, במלואן, ועם דפי שיעורי הבית שהעלית. כמו התצוגה הזו, היא משמיטה את התשובות, הרמזים וההסברים של DaVinci למשוב, שאותם רואים בדפים שלהם, ואת ההגדרות של DaVinci עצמו. הקובץ נבנה כשמבקשים אותו ולא נשמר אחר כך.',
    hide: 'הסתרת הנתונים',
    loading: 'טעינת הנתונים…',
    tabsLabel: 'הנתונים שלי',
    observations: 'מה שנעשה',
    inferences: 'מה ש-DaVinci הסיק',
    pages: 'העמודים שהועלו',
    identity: 'זהות והסכמות',
    observationsNote:
      'רשומות של מה שנעשה בפועל: העלאות, אישורים ותיקונים, עזרה שנפתחה, מה שדווח על הביטחון העצמי, השגות וניסיונות תרגול.',
    inferencesNote:
      'מסקנות ש-DaVinci הסיק ממה שנעשה. כל מסקנה מציינת את גרסת המדיניות שלפיה נגזרה. אלה מסקנות של DaVinci ולא דברים שנעשו.',
    conclusion: 'מסקנה של DaVinci',
    policyVersion: 'גרסת מדיניות',
    withheldFeedback: 'עדיין לא נפתח. אפשר לפתוח אותו בדף המשוב שלו; צפייה בו שם נחשבת לעזרה.',
    withheldCheck: 'עדיין לא נפתח. אפשר לפתוח אותו בדף הבדיקה שלו; צפייה בו שם נחשבת לעזרה.',
    withheldContent:
      'DaVinci שומר את זה, אבל לא מציג אותו כאן, כי הצגתו הייתה עזרה. אפשר לפתוח אותו במקום שבו המערכת מציעה אותו (דף המשוב, הבדיקה או שאלת התרגול); צפייה בו שם נחשבת לעזרה.',
    withheldSource: 'טקסט שנלקח מספר לימוד או ממקור אחר נשמר, אבל לא מוצג כאן.',
    truncated: 'מוצגות רק השורות הראשונות של טבלה זו.',
    empty: 'אין כאן רשומות.',
    pagesNote: 'העמודים שהועלו, כפי ש-DaVinci שומר אותם. זו כתב היד עצמו.',
    openImage: 'פתיחת תמונת העמוד',
    pageAlt: (n: string) => `עמוד שהועלה ${n}`,
    account: 'החשבון',
    username: 'שם משתמש',
    displayName: 'שם תצוגה',
    language: 'שפה',
    otherRecords: 'רשומות נוספות',
    consents: 'החלטות ההסכמה',
    purpose: 'מטרה',
    decision: 'החלטה',
    statement: 'גרסת ההצהרה',
  },
});

type Copy = ReturnType<typeof useMessages<typeof messages.en>>;

const withheldText = (code: NonNullable<MyDataTable['rows'][number]['withheld']>, t: Copy): string =>
  code === 'feedback_unopened'
    ? t.withheldFeedback
    : code === 'check_unopened'
      ? t.withheldCheck
      : code === 'source_text_excluded'
        ? t.withheldSource
        : t.withheldContent;

function Fields({ fields, t }: { fields: readonly { name: string; value: string }[]; t: Copy }): React.JSX.Element {
  return (
    <dl className="mydata-fields">
      {fields.map((field) => (
        <div key={field.name}>
          <dt>{field.name === 'policy_version' ? t.policyVersion : field.name.replace(/_/g, ' ')}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Tables({ tables, kind, t }: { tables: readonly MyDataTable[]; kind: 'observation' | 'inference'; t: Copy }): React.JSX.Element {
  const { locale } = useLocale();
  if (tables.length === 0) return <p className="page-note">{t.empty}</p>;
  return (
    <>
      {tables.map((table) => (
        <details key={table.table} className={`mydata-table mydata-${kind}`}>
          <summary>{friendlyTableLabel(table.table, table.label, locale)}</summary>
          {table.truncated ? <p className="field-hint">{t.truncated}</p> : null}
          <ul className="plain-list">
            {table.rows.map((row, index) => (
              <li key={`${row.id}-${String(index)}`}>
                {kind === 'inference' ? <strong className="mydata-conclusion">{t.conclusion}</strong> : null}
                {row.when !== null ? <time className="mydata-when"> {row.when}</time> : null}
                <Fields fields={row.fields} t={t} />
                {row.withheld !== null ? <p className="field-hint mydata-withheld">{withheldText(row.withheld, t)}</p> : null}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </>
  );
}

function MyDataTabs({ data, t }: { data: MyDataResponse; t: Copy }): React.JSX.Element {
  const [tab, setTab] = useState<TabId>('observations');
  const baseId = useId();
  const label: Record<TabId, string> = { observations: t.observations, inferences: t.inferences, pages: t.pages, identity: t.identity };

  const move = (event: React.KeyboardEvent, from: number): void => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    // Logical order: in a right-to-left page the arrows are mirrored by the browser's own direction, not here.
    const next = TABS[(from + step + TABS.length) % TABS.length] as TabId;
    setTab(next);
    document.getElementById(`${baseId}-tab-${next}`)?.focus();
  };

  return (
    <div className="mydata">
      <div role="tablist" aria-label={t.tabsLabel} className="mydata-tabs">
        {TABS.map((id, index) => (
          <button
            key={id}
            type="button"
            role="tab"
            data-tab={id}
            id={`${baseId}-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`${baseId}-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => move(event, index)}
          >
            {label[id]}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${baseId}-panel-${tab}`} aria-labelledby={`${baseId}-tab-${tab}`} className="mydata-panel">
        {tab === 'observations' ? (
          <>
            <p className="page-note">{t.observationsNote}</p>
            <Tables tables={data.observations} kind="observation" t={t} />
          </>
        ) : null}
        {tab === 'inferences' ? (
          <>
            <p className="page-note">{t.inferencesNote}</p>
            <p className="page-note mydata-scope">{data.scopeOfClaim}</p>
            <Tables tables={data.inferences} kind="inference" t={t} />
          </>
        ) : null}
        {tab === 'pages' ? (
          <>
            <p className="page-note">{t.pagesNote}</p>
            {data.pages.length === 0 ? (
              <p className="page-note">{t.empty}</p>
            ) : (
              <ul className="plain-list">
                {data.pages.map((page) => (
                  <li key={page.pageId}>
                    {page.uploadedAt !== null ? <time className="mydata-when">{page.uploadedAt}</time> : null}
                    <div>
                      <img className="mydata-page" src={page.imageUrl} alt={t.pageAlt(page.pageId)} loading="lazy" />
                    </div>
                    <a href={page.imageUrl} target="_blank" rel="noreferrer">
                      {t.openImage}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
        {tab === 'identity' ? (
          <>
            <h3>{t.account}</h3>
            <dl className="mydata-fields">
              <div>
                <dt>{t.username}</dt>
                <dd>{data.identity.username ?? ''}</dd>
              </div>
              <div>
                <dt>{t.displayName}</dt>
                <dd>{data.identity.displayName ?? ''}</dd>
              </div>
              <div>
                <dt>{t.language}</dt>
                <dd>{data.identity.language ?? ''}</dd>
              </div>
            </dl>
            <h3>{t.consents}</h3>
            {data.consents.length === 0 ? (
              <p className="page-note">{t.empty}</p>
            ) : (
              <ul className="plain-list">
                {data.consents.map((consent, index) => (
                  <li key={`${consent.recordedAt}-${String(index)}`}>
                    <time className="mydata-when">{consent.recordedAt}</time>
                    <dl className="mydata-fields">
                      <div>
                        <dt>{t.purpose}</dt>
                        <dd>{consent.purpose}</dd>
                      </div>
                      <div>
                        <dt>{t.decision}</dt>
                        <dd>{consent.decision}</dd>
                      </div>
                      <div>
                        <dt>{t.statement}</dt>
                        <dd>{consent.statementVersion}</dd>
                      </div>
                    </dl>
                  </li>
                ))}
              </ul>
            )}
            {data.identity.tables.length > 0 ? <h3>{t.otherRecords}</h3> : null}
            <Tables tables={data.identity.tables} kind="observation" t={t} />
          </>
        ) : null}
      </div>
    </div>
  );
}

export function MyDataView(): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const headingId = useId();
  const [open, setOpen] = useState(false);
  // Enabled only once asked for. gcTime 0: a copy of everything about the learner is not kept in the cache after the section closes.
  const query = useQuery({ queryKey: queryKeys.myData, queryFn: () => api.getMyData(), enabled: open, gcTime: 0 });
  // The download (#643): fetched only when asked for, handed straight to the browser's save dialog, never kept in the cache.
  const download = useMutation({
    mutationFn: async () => {
      const blob = await api.downloadMyData();
      saveFile({ blob, fileName: 'davinci-my-data.zip' });
    },
  });

  return (
    <section className="panel mydata-card" aria-labelledby={headingId}>
      <h2 id={headingId}>{t.heading}</h2>
      <p>{t.intro}</p>
      <div className="settings-actions">
      <button type="button" className="settings-button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        {open ? t.hide : t.show}
      </button>
      <button type="button" className="button-secondary settings-button" onClick={() => download.mutate()} disabled={download.isPending}>
        {download.isPending ? t.downloading : t.download}
      </button>
      </div>
      <p className="field-hint">{t.downloadIntro}</p>
      {download.isError ? <FailureNotice error={download.error} onRetry={() => download.mutate()} /> : null}
      {open ? (
        <div className="panel-result">
          {query.isPending ? <p role="status">{t.loading}</p> : null}
          {query.isError ? <FailureNotice error={query.error} onRetry={() => void query.refetch()} /> : null}
          {query.isSuccess ? <MyDataTabs data={query.data} t={t} /> : null}
        </div>
      ) : null}
    </section>
  );
}
